import React from 'react'
import Tooltip, { TooltipProps } from '@mui/material/Tooltip'
import { Copy } from '@/assets/icons'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch } from '@/store/store-hooks'

interface StringTooltipProps extends Omit<TooltipProps, 'children'> {
  children: React.ReactElement
  title: string
  copyToClipboard?: boolean
}

export const StringTooltip: React.FC<StringTooltipProps> = ({ children, title, copyToClipboard = false, ...props }) => {
  const dispatch = useAppDispatch()

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    await navigator.clipboard.writeText(title)
    dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: 'Text copied to clipboard', durationMs: kToastDurationMediumMs }))
  }

  // Don't render tooltip if title is empty, null, or undefined
  if (!title || title.trim() === '') {
    return children
  }

  const tooltipContent = (
    <div className="flex items-center gap-2">
      <span>{title}</span>
      <button
        onClick={handleCopy}
        className="p-1 pr-0"
        onMouseDown={(e) => e.preventDefault()} // Prevent tooltip from closing on copy
      >
        <Copy className="w-4 h-4 text-brand-neutral-600" />
      </button>
    </div>
  )

  return (
    <Tooltip
      title={copyToClipboard ? tooltipContent : title}
      slotProps={{
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, -6] } }],
          slotProps: {
            root: {
              className: '!mx-4',
            },
          },
        },
        tooltip: {
          className:
            'flex items-center min-h-[40px] min-w-fit break-all sm:break-normal px-3 py-1.5 !bg-white !text-xs !rounded-lg !text-brand-neutral-800 !font-sans border border-brand-neutral-200 shadow-[0_0_16px_0_rgba(0,0,0,0.10)]',
        },
        arrow: {
          className: '!text-white [&::before]:border [&::before]:border-brand-neutral-200',
        },
      }}
      {...props}
    >
      {children}
    </Tooltip>
  )
}

export default StringTooltip
